import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { X } from 'lucide-react';
import { Formik, Form, FormikProps } from 'formik';
import { LoaderButton } from 'components/common/LoadingButton';
import FormInput from 'components/common/FormInput';
import PhoneInput from 'components/common/PhoneInput';
import { CustomSelect } from 'components/common/CustomSelect';
import TagInput from 'components/common/TagInput';
import { useAppSelector } from 'store';
import { selectAuthUser } from 'store/slices/auth/auth.selector';
import { useContactOwners } from 'service/crm';
import { ContactOwner } from 'types/crm/contacts';
import { useGetCountries } from 'service/location/useLocation';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
/* @ts-ignore */
import CountryList from 'country-list-with-dial-code-and-flag';
import { CompaniesModalProps, CompanyFormValues, INDUSTRY_OPTIONS } from 'types/crm/company';
import { companyValidationSchema } from 'utils/validation';
import FormTextarea from 'components/common/FormTextArea';




const CompaniesModal: React.FC<CompaniesModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSaving = false,
  editingData,
}) => {
  const authUser = useAppSelector(selectAuthUser);
  const { data: owners = [], isLoading: ownersLoading } = useContactOwners(authUser?.orgUuid as string);

  const { data: countryCode } = useGetCountries();
  const countries = useMemo(() => CountryList.getAll(), []);

  const [phoneCode, setPhoneCode] = useState('+1');
  const [phoneNumberVal, setPhoneNumberVal] = useState('');

  const initialValues = useMemo<CompanyFormValues>(
    () => ({
      title: editingData?.title || '',
      industry: editingData?.industry || '',
      city: editingData?.city || '',
      website: editingData?.website || '',
      phone: editingData?.phone || '',
      companyOwnerUuid: editingData?.companyOwnerUuid || authUser?.appUserUuid || '',
      linkedin: editingData?.linkedin || '',
      tags: editingData?.tags || [],
      description: editingData?.description || '',
    }),
    [editingData, authUser?.appUserUuid],
  );

  // Seed the phone code/number state from the incoming value (edit mode) or the
  // detected country (create mode) each time the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    const rawNum = editingData?.phone || '';
    if (rawNum) {
      const parsed = parsePhoneNumberFromString(rawNum);
      if (parsed) {
        setPhoneCode(`+${parsed.countryCallingCode}`);
        setPhoneNumberVal(parsed.nationalNumber);
      } else {
        const matchedCountry = countries.find((c: any) => rawNum.startsWith(c.dial_code));
        if (matchedCountry) {
          setPhoneCode(matchedCountry.dial_code);
          setPhoneNumberVal(rawNum.slice(matchedCountry.dial_code.length));
        } else {
          setPhoneNumberVal(rawNum);
        }
      }
    } else {
      setPhoneNumberVal('');
      if (countryCode?.country_calling_code) setPhoneCode(countryCode.country_calling_code);
    }
  }, [isOpen, editingData?.phone, countries, countryCode]);

  const ownerOptions = owners.map((o: ContactOwner) => ({
    value: o.uuid,
    label: `${o.name}`.trim(),
  }));

  return createPortal(
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[9999]" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-3xl transform overflow-hidden rounded-2xl bg-[#131720] border border-primary-subtle text-left align-middle shadow-2xl transition-all relative">
                {/* Header */}
                <div className="flex items-center justify-between px-7 py-5 border-b border-primary-subtle">
                  <DialogTitle as="h3" className="text-neutral text-[20px] font-semibold tracking-wide">
                    {editingData ? 'Edit Company' : 'Add New Company'}
                  </DialogTitle>
                  <button
                    type="button"
                    className="flex items-center justify-center w-8 h-8 rounded-lg border border-primary-subtle text-neutral-medium hover:text-neutral hover:border-primary-subtle transition-colors focus:outline-none"
                    onClick={onClose}
                  >
                    <X size={18} />
                  </button>
                </div>

                <Formik
                  initialValues={initialValues}
                  enableReinitialize
                  validationSchema={companyValidationSchema}
                  onSubmit={async (values, { setSubmitting }) => {
                    try {
                      await onSubmit?.(values);
                    } finally {
                      setSubmitting(false);
                    }
                  }}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleBlur,
                    handleSubmit,
                    getFieldProps,
                    setFieldValue,
                    isValid,
                    dirty,
                  }: FormikProps<CompanyFormValues>) => {
                    const handlePhoneCodeChange = (newCode: string) => {
                      setPhoneCode(newCode);
                      setFieldValue('phone', phoneNumberVal ? `${newCode}${phoneNumberVal}` : '');
                    };

                    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                      const cleaned = e.target.value.replace(/\D/g, '').slice(0, 11);
                      setPhoneNumberVal(cleaned);
                      setFieldValue('phone', cleaned ? `${phoneCode}${cleaned}` : '');
                    };

                    return (
                      <Form onSubmit={handleSubmit}>
                        <div className="px-7 pt-8 pb-6 space-y-5">
                          {/* Title */}
                          <FormInput
                            {...getFieldProps('title')}
                            label="Company Name"
                            required
                            placeholder="e.g. Workfall"
                            error={touched.title && errors.title ? (errors.title as string) : undefined}
                          />

                          {/* Industry + City/Location */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <CustomSelect
                              label="Industry"
                              name="industry"
                              options={INDUSTRY_OPTIONS}
                              onBlur={handleBlur}
                              placeholder="Select Industry"
                            />
                            <FormInput
                              {...getFieldProps('city')}
                              label="City/Location"
                              placeholder="Enter city or location"
                              error={touched.city && errors.city ? (errors.city as string) : undefined}
                            />
                          </div>

                          {/* Website + Phone */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <FormInput
                              {...getFieldProps('website')}
                              label="Website"
                              placeholder="https://www.example.com"
                              error={touched.website && errors.website ? (errors.website as string) : undefined}
                            />
                            <PhoneInput
                              label="Phone"
                              name="phone"
                              value={phoneNumberVal}
                              code={phoneCode}
                              data={countries}
                              setCode={handlePhoneCodeChange}
                              onChange={handlePhoneChange}
                              onBlur={handleBlur}
                              error={touched.phone && errors.phone ? (errors.phone as string) : undefined}
                              placeholder="Enter phone number"
                              className="text-neutral-faded"
                            />
                          </div>

                          {/* Owner + LinkedIn */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <CustomSelect
                              label="Owner"
                              name="companyOwnerUuid"
                              options={ownerOptions}
                              onBlur={handleBlur}
                              placeholder={ownersLoading ? 'Loading owners...' : 'Select owner'}
                            />
                            <FormInput
                              {...getFieldProps('linkedin')}
                              label="LinkedIn"
                              placeholder="e.g. www.linkedin.com"
                              error={touched.linkedin && errors.linkedin ? (errors.linkedin as string) : undefined}
                            />
                          </div>

                          {/* Tags */}
                          <div>
                            <label className="block text-[14px] font-normal text-neutral-medium mb-[11px] leading-[100%]">
                              Tags
                            </label>
                            <TagInput
                              tags={values.tags}
                              onChange={(tags) => setFieldValue('tags', tags)}
                              placeholder="Add a tag and press enter"
                              className='bg-primary-intemediate'
                            />
                          </div>

                          {/* Description */}
                          {/* <div>
                            <label
                              htmlFor="description"
                              className="block text-[14px] font-normal text-neutral-faded mb-[11px] leading-[100%]"
                            >
                              Insights
                            </label>
                            <textarea
                              {...getFieldProps('description')}
                              id="description"
                              rows={4}
                              placeholder="Additional info..."
                              className="w-full bg-primary-intemediate border border-primary-subtle rounded-lg px-4 py-3 text-neutral placeholder:text-[#374151] text-sm focus:outline-none focus:border-blue-500/50 resize-none transition-colors"
                            />
                            {touched.description && errors.description ? (
                              <div className="text-red-500 text-xs mt-1">{errors.description as string}</div>
                            ) : null}
                          </div>
                        </div> */}
                          <FormTextarea
                            name="description"
                            label="Insights"
                            placeholder="Add insights about this company"
                            maxLength={500}
                            darkBg
                            className={{ label: 'text-sm', textarea: 'text-sm' }}
                          />
                        </div>
                        {/* Footer */}
                        <div className="flex items-center justify-end gap-3 px-7 py-4 border-t border-primary-subtle">
                          <button
                            type="button"
                            onClick={onClose}
                            disabled={isSaving}
                            className="py-3 px-5 rounded-lg border text-sm border-primary-subtle"
                          >
                            Cancel
                          </button>
                          <LoaderButton
                            type="submit"
                            disabled={!isValid || !dirty || isSaving}
                            className="!m-0 !py-2.5 !px-6 "
                            text={editingData ? 'Save Changes' : 'Create Company'}
                            isLoading={isSaving}
                          />
                        </div>
                      </Form>
                    );
                  }}
                </Formik>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>,
    document.body
  );
};

export default CompaniesModal;
